import { Injectable, inject } from '@angular/core';
import {
  DEFUALT_SEARCH,
  ICenterSelect,
  IPagedSearch,
  mongoQueryParser,
} from '@evencel/models';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { map, mergeMap, tap } from 'rxjs';
import { CenterService } from '../../services';
import * as actions from './center.actions';

interface CenterStateModel {
  selectables: ICenterSelect[];
}

@State<CenterStateModel>({
  name: 'center',
  defaults: {
    selectables: [],
  },
})
@Injectable({ providedIn: 'root' })
export class CenterState {
  centerService = inject(CenterService);

  @Selector() static selectables(state: CenterStateModel) {
    return state.selectables;
  }

  @Action(actions.CreateCenter)
  create({ dispatch }: StateContext<void>, { center }: actions.CreateCenter) {
    return this.centerService
      .post(center)
      .pipe(mergeMap((data) => dispatch(new actions.GetCenterResponse(data))));
  }

  @Action(actions.UpdateCenter)
  update({ dispatch }: StateContext<void>, { center }: actions.UpdateCenter) {
    return this.centerService
      .patch(center)
      .pipe(mergeMap((data) => dispatch(new actions.GetCenterResponse(data))));
  }

  @Action(actions.ActivateCenter)
  activate(
    { dispatch }: StateContext<void>,
    { center }: actions.ActivateCenter
  ) {
    return this.centerService
      .activate(center)
      .pipe(tap((data) => dispatch(new actions.GetCenterResponse(data))));
  }

  @Action(actions.DeleteCenter)
  delete(context: StateContext<void>, { center }: actions.DeleteCenter) {
    return this.centerService.delete(center._id);
  }

  @Action(actions.RestoreCenter)
  restore(context: StateContext<void>, { center }: actions.RestoreCenter) {
    return this.centerService.restore(center._id);
  }

  @Action(actions.GetCenter)
  getOne({ dispatch }: StateContext<void>, { id }: actions.GetCenter) {
    return this.centerService
      .getOne(id)
      .pipe(tap((data) => dispatch(new actions.GetCenterResponse(data))));
  }

  @Action(actions.SearchCenters)
  get({ dispatch }: StateContext<void>, { data }: actions.SearchCenters) {
    let obs = null;
    if (data.condition) obs = this.centerService.search(data);
    else obs = this.centerService.get(data);

    return obs.pipe(
      tap((data) =>
        dispatch(new actions.GetCentersResponse(data, { state: 'success' }))
      )
    );
  }

  @Action(actions.GetCenterList)
  getAll({ dispatch }: StateContext<void>) {
    return this.centerService
      .getAll()
      .pipe(tap((data) => dispatch(new actions.GetCenterListResponse(data))));
  }
  
  @Action(actions.SearchSelectableCenters)
  searchSelectableCenters(
    { patchState }: StateContext<CenterStateModel>,
    { text }: actions.SearchSelectableCenters
  ) {
    const q = { acronym: { $regex: text } };
    const condition = mongoQueryParser.parse(q);
    const page: IPagedSearch = {
      ...DEFUALT_SEARCH,
      condition,
      projection: 'select',
    };

    return this.centerService.search<ICenterSelect>(page).pipe(
      map((d) => d.data),
      tap((selectables) => patchState({ selectables }))
    );
  }
}
